/**
 * A QR code, drawn from a string, with no dependency and no network. F-141.
 *
 * **Why this is written out rather than installed.** The desktop half of the first run has to put
 * the address on a phone, and the only control that does that without typing is a QR. Every way of
 * getting one that is not this costs something the app cannot pay: a rendering service means the
 * learner's address goes to a third party on a screen whose whole subject is how they will be
 * reached; a script from a CDN is a second origin to trust and a request that can fail on the one
 * screen that must not; a package is a dependency carried into a bundle loaded over a phone
 * connection for one picture. The encoder is about two hundred lines of arithmetic that has not
 * changed since 2006 and it is pure, so it is asserted from plain Node against a reference
 * implementation rather than looked at.
 *
 * **Deliberately a narrow slice of the format**, which is the thing that keeps it small enough to
 * be worth having here:
 *
 * - **Byte mode only.** The input is a URL. Alphanumeric mode would pack an upper-case URL more
 *   tightly and would silently mangle a lower-case one, which is every URL this will ever be given.
 * - **Error correction level L**, the lowest. A QR on a screen, scanned from thirty centimetres by
 *   a phone that is being held still, is not the damaged-label case the higher levels exist for,
 *   and L is what keeps the version — and therefore the module count — small enough to scan from a
 *   laptop display.
 * - **Versions 1 to 5 only**, which carries **106 bytes** of payload — version 5 holds 108 data
 *   codewords at level L and the mode and length indicators spend two of them, which is what
 *   `QR_MAX_BYTES` is. That is far more than any address this will carry, and it is the range in
 *   which **level L uses exactly one error-correction block** — so there is no block interleaving
 *   in this file at all. Version 6 is where that stops being true, which is why the ceiling is
 *   where it is rather than at a round number.
 *
 * Anything longer answers `undefined` rather than throwing or truncating. The caller shows the
 * address as text instead, which is a worse control and not a broken screen — and a truncated URL
 * in a QR is the one outcome that is worse than no QR, because it scans.
 */

/** A square of modules, row-major. `true` is dark. */
export interface QrMatrix {
  /** Modules per side, quiet zone excluded. 21 at version 1, rising by 4 a version. */
  size: number;
  /** `modules[row][column]`. */
  modules: boolean[][];
}

/**
 * How many data codewords each version holds at level L, and how many correct them.
 *
 * Written out rather than derived. The derivation exists — raw module count, minus the function
 * patterns, divided by eight — and it is the kind of formula that is right for the five versions
 * anybody tests and wrong for the sixth. A table over a closed range cannot be wrong about a
 * version it does not contain: it answers `undefined` and the caller gives up, which is the
 * failure this range is chosen to make impossible.
 */
const VERSIONS: readonly { version: number; data: number; ec: number }[] = [
  { version: 1, data: 19, ec: 7 },
  { version: 2, data: 34, ec: 10 },
  { version: 3, data: 55, ec: 15 },
  { version: 4, data: 80, ec: 20 },
  { version: 5, data: 108, ec: 26 },
];

/** The most bytes `encodeQr` will take. Beyond it the caller must show the address as text. */
export const QR_MAX_BYTES = 108 - 2;

/* ---- GF(256), the field the error correction is computed in ---- */

/**
 * Log and antilog tables for GF(2⁸) under the QR primitive polynomial `x⁸+x⁴+x³+x²+1` (0x11D).
 *
 * Built once at module load. Multiplication in this field is addition of logarithms, which is the
 * whole reason the tables exist: the alternative is a carry-less multiply and a modular reduction
 * per coefficient, run about three thousand times for one code.
 */
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  // Doubled, so a product of two logarithms can be looked up without a modulo on every multiply.
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255] as number;
}

function mul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[(LOG[a] as number) + (LOG[b] as number)] as number;
}

/**
 * The generator polynomial for `degree` error-correction codewords: ∏(x − α^i) for i < degree.
 *
 * **Coefficients are held highest-power-first**, which is the order the division below consumes
 * them in, and the two lines of the multiply are what decide that. Written the other way round —
 * which is how this was written first — every finder, every timing pattern, the whole data region
 * and the format bits are correct, and only the last seven codewords of the symbol are wrong. It
 * scans. It decodes to nothing. Nothing about the picture says which of about two hundred lines
 * was at fault, which is why `packages/web/test/onboarding-qr.test.ts` asserts whole symbols
 * against a reference implementation rather than asserting anything about this function.
 */
function generator(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i += 1) {
    // Multiply by (x + α^i): the `x` term raises each power by one, which in a highest-first
    // array is the *same* index in an array one longer; the constant term moves one along.
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] = (next[j] as number) ^ (poly[j] as number);
      next[j + 1] = (next[j + 1] as number) ^ mul(poly[j] as number, EXP[i] as number);
    }
    poly = next;
  }
  return poly;
}

/** The remainder of `data` divided by the generator: the error-correction codewords. */
function errorCorrection(data: readonly number[], degree: number): number[] {
  const gen = generator(degree);
  const remainder = new Array<number>(degree).fill(0);
  for (const byte of data) {
    const factor = byte ^ (remainder.shift() as number);
    remainder.push(0);
    for (let i = 0; i < degree; i += 1) {
      remainder[i] = (remainder[i] as number) ^ mul(gen[i + 1] as number, factor);
    }
  }
  return remainder;
}

/* ---- The symbol ---- */

/** Whether the module at this position is a function pattern rather than data. */
type Reserved = boolean[][];

function blank(size: number): boolean[][] {
  return Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
}

/**
 * The finder pattern and its separator, as one nine-by-nine stamp clipped to the symbol.
 *
 * The separator is the ring at Chebyshev distance four, and drawing it here rather than as its own
 * pass is what makes the three corners one piece of code: the clip is what turns the same stamp
 * into the three different L-shapes the corners actually need.
 */
function finder(modules: boolean[][], reserved: Reserved, cx: number, cy: number): void {
  const size = modules.length;
  for (let dy = -4; dy <= 4; dy += 1) {
    for (let dx = -4; dx <= 4; dx += 1) {
      const x = cx + dx;
      const y = cy + dy;
      if (x < 0 || x >= size || y < 0 || y >= size) continue;
      const dist = Math.max(Math.abs(dx), Math.abs(dy));
      (modules[y] as boolean[])[x] = dist !== 2 && dist !== 4;
      (reserved[y] as boolean[])[x] = true;
    }
  }
}

function alignment(modules: boolean[][], reserved: Reserved, cx: number, cy: number): void {
  for (let dy = -2; dy <= 2; dy += 1) {
    for (let dx = -2; dx <= 2; dx += 1) {
      (modules[cy + dy] as boolean[])[cx + dx] = Math.max(Math.abs(dx), Math.abs(dy)) !== 1;
      (reserved[cy + dy] as boolean[])[cx + dx] = true;
    }
  }
}

/**
 * Everything that is not data: finders, separators, timing, the alignment pattern, the format
 * information's seats and the one module that is always dark.
 *
 * The format bits themselves are written last, after a mask has been chosen, because the mask is
 * three of the fifteen bits. What this reserves is their *seats*, so the data placement below skips
 * them — a data bit written into a format seat is overwritten later and every bit after it in the
 * stream lands one position early, which is a symbol that scans and decodes to nothing.
 */
function functionPatterns(version: number): { modules: boolean[][]; reserved: Reserved } {
  const size = version * 4 + 17;
  const modules = blank(size);
  const reserved = blank(size);

  for (let i = 0; i < size; i += 1) {
    (modules[6] as boolean[])[i] = i % 2 === 0;
    (reserved[6] as boolean[])[i] = true;
    (modules[i] as boolean[])[6] = i % 2 === 0;
    (reserved[i] as boolean[])[6] = true;
  }

  finder(modules, reserved, 3, 3);
  finder(modules, reserved, size - 4, 3);
  finder(modules, reserved, 3, size - 4);

  // Versions 2 to 5 carry exactly one alignment pattern, opposite the top-left finder. The general
  // rule produces a grid of them; at these versions the grid is two by two and three of its four
  // cells are the finder corners, which take precedence.
  if (version >= 2) alignment(modules, reserved, size - 7, size - 7);

  // The format information's two copies, reserved and not yet written.
  for (let i = 0; i < 9; i += 1) {
    (reserved[8] as boolean[])[i] = true;
    (reserved[i] as boolean[])[8] = true;
  }
  for (let i = 0; i < 8; i += 1) {
    (reserved[8] as boolean[])[size - 1 - i] = true;
    (reserved[size - 1 - i] as boolean[])[8] = true;
  }

  // Always dark, and it is a function module: leaving it writable costs one data bit its place.
  (modules[size - 8] as boolean[])[8] = true;
  (reserved[size - 8] as boolean[])[8] = true;

  return { modules, reserved };
}

/** The eight mask conditions, by number. `x` is the column and `y` the row. */
const MASKS: readonly ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

/**
 * How badly a masked symbol reads, by the four rules in the specification.
 *
 * The rules are about a scanner rather than about a person: runs of five or more confuse the
 * sampling grid, two-by-two blocks blur, the finder-like sequence is mistaken for a finder, and a
 * symbol that is mostly one colour has no contrast to threshold against. The lowest score wins.
 *
 * **Implemented rather than skipped, and the reason is not conformance.** Any of the eight masks
 * produces a valid symbol — the mask number is carried in the format bits — so a fixed choice would
 * scan most of the time. It would also, for some particular address, produce a symbol with a
 * finder-like run through the middle of it, and the failure would belong to one learner's URL and
 * no other. That is not a bug anybody finds twice.
 */
function penalty(modules: readonly boolean[][]): number {
  const size = modules.length;
  let score = 0;

  // Rule 1: runs of five or more of the same colour, in both directions.
  for (let i = 0; i < size; i += 1) {
    for (const horizontal of [true, false]) {
      let run = 1;
      for (let j = 1; j < size; j += 1) {
        const here = horizontal
          ? ((modules[i] as boolean[])[j] as boolean)
          : ((modules[j] as boolean[])[i] as boolean);
        const before = horizontal
          ? ((modules[i] as boolean[])[j - 1] as boolean)
          : ((modules[j - 1] as boolean[])[i] as boolean);
        if (here === before) {
          run += 1;
          if (run === 5) score += 3;
          else if (run > 5) score += 1;
        } else {
          run = 1;
        }
      }
    }
  }

  // Rule 2: every two-by-two block of one colour.
  for (let y = 0; y < size - 1; y += 1) {
    for (let x = 0; x < size - 1; x += 1) {
      const a = (modules[y] as boolean[])[x] as boolean;
      if (
        a === ((modules[y] as boolean[])[x + 1] as boolean) &&
        a === ((modules[y + 1] as boolean[])[x] as boolean) &&
        a === ((modules[y + 1] as boolean[])[x + 1] as boolean)
      ) {
        score += 3;
      }
    }
  }

  /*
   * Rule 3: the 1:1:3:1:1 sequence a scanner reads as a finder, preceded or followed by four
   * light modules.
   *
   * **The symbol's own edge counts as the light area**, which is the part of this rule that is
   * easy to get wrong and was got wrong here first. The quiet zone outside the symbol is light by
   * definition — `qrPath` draws four modules of it — so a finder-like run flush against the right
   * edge of the symbol *is* followed by light and is exactly as confusing to a scanner as one in
   * the middle. Written as a literal eleven-module window instead, a run at the border matches
   * nothing, the mask that leaves one there scores too well, and the chosen mask is the one with a
   * decoy finder on its edge — the single case this rule exists to prevent.
   *
   * After a hit the scan resumes past the whole pattern; after a miss it resumes four along, which
   * is the next position the sequence could start at given its own overlap.
   */
  const pattern = [true, false, true, true, true, false, true];
  const allLight = (line: readonly boolean[], from: number, to: number): boolean => {
    for (let i = Math.max(from, 0); i < Math.min(to, size); i += 1) if (line[i]) return false;
    return true;
  };
  const scan = (line: readonly boolean[]): number => {
    let found = 0;
    let at = 0;
    while (at + 7 <= size) {
      let matches = true;
      for (let k = 0; k < 7; k += 1) {
        if (line[at + k] !== (pattern[k] as boolean)) {
          matches = false;
          break;
        }
      }
      if (!matches) {
        at += 1;
        continue;
      }
      if (allLight(line, at - 4, at) || allLight(line, at + 7, at + 11)) {
        found += 40;
        at += 7;
      } else {
        at += 4;
      }
    }
    return found;
  };
  for (let i = 0; i < size; i += 1) {
    score += scan(modules[i] as boolean[]);
    score += scan(modules.map((row) => (row as boolean[])[i] as boolean));
  }

  // Rule 4: how far the proportion of dark modules is from half, in steps of five per cent.
  let dark = 0;
  for (const row of modules) for (const module of row) if (module) dark += 1;
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/**
 * The fifteen format bits: two for the level, three for the mask, ten of BCH, masked by 0x5412.
 *
 * The final XOR is what stops an all-zero format — level M with mask 0 — from being a blank strip
 * that a scanner reads as damage.
 */
function formatBits(mask: number): number {
  // 0b01 is level L. The two-bit encoding is not the level's ordinal: L is 01, M is 00.
  const data = (0b01 << 3) | mask;
  let remainder = data;
  for (let i = 0; i < 10; i += 1) {
    remainder = (remainder << 1) ^ ((remainder >> 9) * 0x537);
  }
  return ((data << 10) | remainder) ^ 0x5412;
}

function placeFormat(modules: boolean[][], mask: number): void {
  const size = modules.length;
  const bits = formatBits(mask);
  const bit = (i: number): boolean => ((bits >> i) & 1) === 1;

  for (let i = 0; i <= 5; i += 1) (modules[i] as boolean[])[8] = bit(i);
  (modules[7] as boolean[])[8] = bit(6);
  (modules[8] as boolean[])[8] = bit(7);
  (modules[8] as boolean[])[7] = bit(8);
  for (let i = 9; i < 15; i += 1) (modules[8] as boolean[])[14 - i] = bit(i);

  for (let i = 0; i < 8; i += 1) (modules[8] as boolean[])[size - 1 - i] = bit(i);
  for (let i = 8; i < 15; i += 1) (modules[size - 15 + i] as boolean[])[8] = bit(i);
}

/**
 * A QR code for this text, or `undefined` if it does not fit in the five versions above.
 *
 * The text is encoded as UTF-8 before anything else, so an accented character costs two bytes and
 * an emoji four. Nothing in this app puts either in a URL, and getting it wrong by measuring
 * `length` instead would produce a symbol one byte short of its own declared count — which is a
 * checksum failure rather than a wrong character, so it would not scan at all rather than scanning
 * wrongly. That is the right direction, and it is still worth not being wrong about.
 */
export function encodeQr(text: string): QrMatrix | undefined {
  const bytes = new TextEncoder().encode(text);
  const chosen = VERSIONS.find((candidate) => candidate.data >= bytes.length + 2);
  if (!chosen) return undefined;

  /*
   * The bit stream: mode, length, the bytes, a terminator, then a pad to the byte boundary.
   *
   * **Assembled as bits and packed afterwards, rather than as bytes directly.** The header is
   * twelve bits, so every data byte straddles two codewords — the byte-at-a-time version of this
   * is four nibble shifts per byte and it was written that way first. It produced a symbol that
   * scanned and decoded to the right string for every input tested by hand and to nonsense for a
   * length whose low nibble was zero. Bits are slower by an amount nobody can perceive once per
   * screen, and they are the version that is obviously right.
   */
  const bits: number[] = [];
  const pushBits = (value: number, width: number): void => {
    for (let i = width - 1; i >= 0; i -= 1) bits.push((value >> i) & 1);
  };
  pushBits(0b0100, 4); // byte mode
  pushBits(bytes.length, 8); // the count indicator is eight bits for versions 1 to 9
  for (const byte of bytes) pushBits(byte, 8);
  pushBits(0, Math.min(4, chosen.data * 8 - bits.length)); // terminator, truncated if it will not fit
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) byte = (byte << 1) | (bits[i + j] as number);
    codewords.push(byte);
  }
  /*
   * The pad bytes are prescribed — 0xEC then 0x11, alternating — rather than zeroes. Zero padding
   * is legal and produces long runs of one colour, which is what the mask penalty above exists to
   * avoid; the specification's two values break them up before a mask is even chosen.
   */
  let pad = 0xec;
  while (codewords.length < chosen.data) {
    codewords.push(pad);
    pad = pad === 0xec ? 0x11 : 0xec;
  }

  const all = [...codewords, ...errorCorrection(codewords, chosen.ec)];

  const { modules, reserved } = functionPatterns(chosen.version);
  const size = modules.length;

  /*
   * The zigzag: two columns at a time, right to left, alternating up and down.
   *
   * **Column six is skipped by moving the cursor, not by renaming it**, and that distinction is the
   * whole of the comment. The vertical timing pattern owns column six for the symbol's full height,
   * so the pair that would have been (6,5) is (5,4) instead — and every pair after it shifts with
   * it, ending at (1,0). Skipping it by reading a different column for that one iteration and
   * leaving the counter alone gives (5,4) then (4,3), (2,1): column four is written twice, column
   * zero never, and the second write silently replaces the first. It is a one-bit error in the last
   * codeword of the symbol. The code still scans, and it still decodes to nothing.
   */
  let bit = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vertical = 0; vertical < size; vertical += 1) {
      for (let j = 0; j < 2; j += 1) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vertical : vertical;
        if ((reserved[y] as boolean[])[x]) continue;
        if (bit >= all.length * 8) continue;
        (modules[y] as boolean[])[x] = (((all[bit >> 3] as number) >> (7 - (bit & 7))) & 1) === 1;
        bit += 1;
      }
    }
  }

  /*
   * Every mask, scored, lowest wins — and the format bits go on *after* the mask, because the mask
   * number is three of those fifteen bits and masking them would say the wrong number.
   */
  let best: boolean[][] | undefined;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask += 1) {
    const condition = MASKS[mask] as (x: number, y: number) => boolean;
    const candidate = modules.map((row, y) =>
      row.map((module, x) =>
        ((reserved[y] as boolean[])[x] as boolean) ? module : module !== condition(x, y),
      ),
    );
    placeFormat(candidate, mask);
    const score = penalty(candidate);
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return { size, modules: best as boolean[][] };
}

/**
 * The matrix as one SVG path, which is how it reaches a screen.
 *
 * One path of many subpaths rather than a rectangle element per module: a version 3 symbol is 841
 * modules, and about half of them are dark, so the element-per-module version is four hundred DOM
 * nodes on a screen that also has an install card on it. The path is one node and one string.
 *
 * The quiet zone is four modules on every side and is not decoration — a scanner uses it to find
 * the symbol's edge, and a QR drawn flush to a panel's border frequently will not scan at all. The
 * caller gets it inside the returned `viewBox` so there is nothing to remember.
 */
export function qrPath(matrix: QrMatrix): { path: string; viewBox: string } {
  const quiet = 4;
  const parts: string[] = [];
  for (let y = 0; y < matrix.size; y += 1) {
    const row = matrix.modules[y] as boolean[];
    for (let x = 0; x < matrix.size; x += 1) {
      if (row[x]) parts.push(`M${x + quiet} ${y + quiet}h1v1h-1z`);
    }
  }
  const side = matrix.size + quiet * 2;
  return { path: parts.join(''), viewBox: `0 0 ${side} ${side}` };
}
